import { Player, Room, nullCardSet, nullPlayerSet, nullPlayer, nullRoom, HistoryState, RoomAndPlayerUsername, CardSet } from '../Types'
import { Deck, PlayingCard, CardName, Suit } from '../Cards'
import { apiClient } from '../api/client'
import { initRoomFromData } from '../api/utils'

import isObjEqual from 'fast-deep-equal'

/**
 * DEPRECATED: Use apiClient.createRoom() instead
 * Create a new game room
 */
export async function CreateRoom(): Promise<Room> {
    const response = await apiClient.createRoom()
    return initRoomFromData(response.room)
}

/**
 * DEPRECATED: Use apiClient.addPlayer() instead
 * Add a player to a room
 */
export async function CreatePlayer(username: string, gameId: string): Promise<RoomAndPlayerUsername> {
    const response = await apiClient.addPlayer(gameId, username)
    return {
        room: initRoomFromData(response.room),
        username: response.username
    }
}

/**
 * DEPRECATED: Use apiClient.getRoom() instead
 * Get room details
 */
export async function getRoom(gameId: string): Promise<Room> {
    if (gameId === "") {
        return nullRoom
    }
    try {
        const response = await apiClient.getRoom(gameId)
        return initRoomFromData(response.room)
    } catch (error) {
        console.error('Error getting room:', error)
        return nullRoom
    }
}

/**
 * DEPRECATED: Use useSSE hook instead
 * This function is replaced by the useSSE React hook for real-time updates
 */
export async function getRoomOn(gameId: string, callback: any) {
    console.warn('getRoomOn is deprecated. Use useSSE hook instead.')
    // This function should not be used anymore - useSSE hook replaces it
    throw new Error('getRoomOn is deprecated. Use useSSE hook instead.')
}

/**
 * DEPRECATED: Use apiClient.roomExists() instead
 * Check if a room exists
 */
export async function gameRoomExists(gameId: string): Promise<boolean> {
    try {
        const response = await apiClient.roomExists(gameId)
        return response.exists
    } catch (error) {
        console.error('Error checking room existence:', error)
        return false
    }
}

/**
 * DEPRECATED: Use apiClient.removePlayer() instead
 * Remove a player from a room
 */
export async function RemovePlayer(username: string | null, gameId: string) {
    if (username === null) {
        return
    }
    try {
        await apiClient.removePlayer(gameId, username)
    } catch (error) {
        console.error('Error removing player:', error)
    }
}

/**
 * DEPRECATED: Use apiClient.deleteRoom() instead
 * Delete a room
 */
export async function RemoveRoom(gameId: string) {
    try {
        await apiClient.deleteRoom(gameId)
    } catch (error) {
        console.error('Error deleting room:', error)
    }
}

/**
 * DEPRECATED: Use apiClient.setPlayerReady() instead
 * Set player ready status
 */
export const setReadyStatus = async (username: string, gameId: string, readyStatus: boolean) => {
    try {
        await apiClient.setPlayerReady(gameId, username, readyStatus)
    } catch (error) {
        console.error('Error setting ready status:', error)
    }
}

/**
 * DEPRECATED: Use apiClient.startGame() instead
 * Start the game
 */
export const startGame = async (gameId: string): Promise<Room> => {
    try {
        const response = await apiClient.startGame(gameId)
        return initRoomFromData(response.room)
    } catch (error) {
        console.error('Error starting game:', error)
        throw error
    }
}

/**
 * DEPRECATED: Use apiClient.callBluff() instead
 * Call bluff on the current play
 */
export async function callBluff(gameId: string, callingPlayer: Player): Promise<boolean> {
    try {
        const response = await apiClient.callBluff(gameId, callingPlayer.playerId)
        return response.success
    } catch (error) {
        console.error('Error calling bluff:', error)
        return false
    }
}

/**
 * DEPRECATED: Use apiClient.moveCards() instead
 * Move cards after a bluff call
 */
export async function moveCards(gameId: string, showCards: boolean = false) {
    try {
        await apiClient.moveCards(gameId, showCards)
    } catch (error) {
        console.error('Error moving cards:', error)
        throw error
    }
}

/**
 * DEPRECATED: Backend handles this now
 * Process next turn
 */
export async function processNextTurn(gameId: string) {
    try {
        const response = await apiClient.processNextTurn(gameId)
        return initRoomFromData(response.room)
    } catch (error) {
        console.error('Error processing next turn:', error)
        throw error
    }
}

// =============================================================================
// UTILITY FUNCTIONS (Client-side only, no backend calls)
// =============================================================================

/**
 * Get a unique username by appending numbers if needed
 */
export const getUniqueUsername = (username: string, players: Player[]) => {
    let i = 0;
    let possibleName = username
    while (getUsernames(players).indexOf(possibleName) >= 0) {
        possibleName = username + i.toString()
        i++;
    }
    return possibleName
}

/**
 * Client-side card movement logic (deprecated - backend handles this now)
 */
export function moveCardsWithinRoom(room: Room) {
    if (room.bluffCaller.playerId === "") {
        return nullRoom;
    }
    // assumed that lastPlayedCardSet player is truthful
    let user = room.bluffCaller.playerId
    const lastPlayedCardSet = getLastPlayedCardSetWithCards(room)
    let newStarter = lastPlayedCardSet.player
    // checks if the lastPlayedCardSet has a lie within it
    for (let i = 0; i < lastPlayedCardSet.deck.getCount(); i++) {
        if (lastPlayedCardSet.deck.cardAtIndex(i).cardName !== room.roundInfo.roundValue &&
            lastPlayedCardSet.deck.cardAtIndex(i).cardName !== CardName.Joker) {
            user = lastPlayedCardSet.player
            newStarter = room.bluffCaller.playerId
            break
        }
    }
    // transfers the cards to the right player
    for (let i = 0; i < room.order.length; i++) {
        if (room.order[i].playerId === user) {
            room.order[i].hand.deck.addCards(room.cardsInPlay.getCards())
            break
        }
    }
    room.cardsInPlay.takeCards(0)
    return makePlayerNext(room, newStarter)
}

/**
 * Make a player the head of the turn order
 */
export const makePlayerHeadOfOrder = (room: Room, playerId: string) => {
    let arr: Player[] = []
    while (room.order.length > 0) {
        if (room.order[0].playerId === playerId) {
            room.order = room.order.concat(arr)
            console.log(room.order)
            break
        } else {
            let elem = room.order.shift()
            if (elem === undefined) {
                throw new Error("removed from empty order")
            } else {
                arr.push(elem)
            }
        }
    }
}

/**
 * Make a player go next (called before processNextTurn)
 */
export const makePlayerNext = (room: Room, playerId: string) => {
    makePlayerHeadOfOrder(room, playerId)
    let elem = room.order.pop()
    if (elem === undefined) {
        throw new Error("removed from empty order")
    } else {
        room.order.unshift(elem)
    }
    return room
}

/**
 * Finish the current round and reset state
 */
export function finishRound(room: Room) {
    room.bluffCaller = nullPlayer
    room.cardSetFromRound = []
    room.cardsInPlay.takeCards(0)
    room.roundInfo.passNum = -1
    room.roundInfo.roundValue = CardName.Joker
    return room
}

/**
 * Get the number of players who are ready
 */
export const numberReady = (room: Room) => {
    let count = 0
    for (let i = 0; i < room.players.length; i++) {
        if (room.players[i].ready) {
            count++;
        }
    }
    return count
}

/**
 * Get the last played card set
 */
export const getLastPlayedCardSet = (room: Room) => {
    if (room.cardSetFromRound.length === 0) {
        return nullCardSet
    }
    return room.cardSetFromRound[room.cardSetFromRound.length - 1]
}

/**
 * Get the last played card set that has cards
 */
export const getLastPlayedCardSetWithCards = (room: Room) => {
    for (let index = room.cardSetFromRound.length - 1; index > -1; index--) {
        if (room.cardSetFromRound[index].deck.getCount()) {
            return room.cardSetFromRound[index]
        }
    }
    return nullCardSet
}

/**
 * Get all usernames from players array
 */
export const getUsernames = (players: Player[]) => {
    let arr: string[] = []
    for (let i = 0; i < players.length; i++) {
        arr.push(players[i].playerId)
    }
    return arr
}

/**
 * Count the number of real (non-null) players
 */
export const numRealPlayers = (players: Player[]): number => {
    let num = 0
    players.forEach(player => {
        if (!isObjEqual(player, nullPlayer)) {
            num += 1
        }
    });
    return num
}

/**
 * Get the first open index in the players array
 */
const firstOpenPlayerIndex = (players: Player[]): number => {
    for (let i = 0; i < players.length; i++) {
        if (isObjEqual(players[i], nullPlayer)) {
            return i
        }
    }
    return -1
}

/**
 * Shuffle an array in-place
 */
function shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}

// =============================================================================
// HISTORY STATE HELPERS
// =============================================================================

export const getStateAfterSetUsername = (username: string, state: HistoryState) => {
    state["username"] = username
    return state
}

export const getStateAfterSetGameId = (gameId: string, state: HistoryState) => {
    state["gameId"] = gameId
    return state
}

export const getUsername = (state: HistoryState): string => {
    if (!state) {
        return ""
    }
    return (state["username"] || "") as string
}

export const getGameId = (state: { [index: string]: any }) => {
    if (!state) {
        return ""
    }
    return (state["gameId"] || "") as string
}
